import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "./Image";
import { tinaField } from "@/utils";

export default function BeforeAfterSlider({
  pair,
  beforeAlt = "Before treatment",
  afterAlt = "After treatment",
}) {
  const [position, setPosition] = useState(50);
  const [beforeCutoff, setBeforeCutoff] = useState(0);
  const [afterCutoff, setAfterCutoff] = useState(100);
  const containerRef = useRef(null);
  const beforeLabelRef = useRef(null);
  const afterLabelRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const beforeLabel = beforeLabelRef.current;
    const afterLabel = afterLabelRef.current;
    if (!container || !beforeLabel || !afterLabel) return;

    const measure = () => {
      const { left, width } = container.getBoundingClientRect();
      if (!width) return;

      const beforeRight = ((beforeLabel.getBoundingClientRect().right - left) / width) * 100;
      const afterLeft = ((afterLabel.getBoundingClientRect().left - left) / width) * 100;

      setBeforeCutoff((current) => (Math.abs(current - beforeRight) < 0.1 ? current : beforeRight));
      setAfterCutoff((current) => (Math.abs(current - afterLeft) < 0.1 ? current : afterLeft));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(beforeLabel);
    observer.observe(afterLabel);
    return () => observer.disconnect();
  }, [pair?.before, pair?.after]);

  const showBeforeLabel = position > beforeCutoff;
  const showAfterLabel = position < afterCutoff;

  const setFromClientX = useCallback((clientX) => {
    const container = containerRef.current;
    if (!container) return;

    const { left, width } = container.getBoundingClientRect();
    const next = ((clientX - left) / width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }, []);

  const onPointerDown = (event) => {
    if (event.target.closest("[data-tina-field]")) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setFromClientX(event.clientX);
  };

  const onPointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    setFromClientX(event.clientX);
  };

  if (pair?.before && !pair?.after) {
    return (
      <div className="relative aspect-[2/1] w-full overflow-hidden rounded-lg bg-gray-200">
        <Image src={pair.before} alt={beforeAlt} className="absolute inset-0 h-full w-full" bg="bg-gray-400" />
        <span 
          className="absolute bottom-3 left-3 z-20 rounded bg-black/50 px-2 py-1 text-xs md:text-sm font-bold text-white"
          data-tina-field={tinaField(pair, "before")}
        >
          Before
        </span>
        <span
          className="absolute bottom-3 right-3 z-20 rounded bg-black/50 px-2 py-1 text-xs md:text-sm font-bold text-white"
          data-tina-field={tinaField(pair, "after")}
        >
          After
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative aspect-[2/1] w-full select-none overflow-hidden rounded-lg bg-gray-200 touch-none !cursor-col-resize"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
    >
      <Image src={pair?.after} alt={afterAlt} className="absolute inset-0 h-full w-full" bg="bg-gray-300" />

      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}>
        <Image src={pair?.before} alt={beforeAlt} className="absolute inset-0 h-full w-full" bg="bg-gray-400" />
      </div>

      <div className="pointer-events-none absolute inset-y-0 z-10 w-px bg-white" style={{ left: `${position}%` }} aria-hidden />

      <div
        className="pointer-events-none absolute top-1/2 z-20 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white bg-white text-primary shadow-sm"
        style={{ left: `${position}%` }}
        aria-hidden
      >
        <ChevronLeft className="h-4 w-4" />
        <ChevronRight className="-ml-1 h-4 w-4" />
      </div>

      <span
        ref={beforeLabelRef}
        className={`absolute bottom-3 left-3 z-20 rounded bg-black/50 px-2 py-1 text-xs md:text-sm font-bold text-white transition-opacity duration-150 ${
          showBeforeLabel ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        data-tina-field={tinaField(pair, "before")}
        aria-hidden={!showBeforeLabel}
        onPointerDown={(event) => event.stopPropagation()}
      >
        Before
      </span>
      <span
        ref={afterLabelRef}
        className={`absolute bottom-3 right-3 z-20 rounded bg-black/50 px-2 py-1 text-xs md:text-sm font-bold text-white transition-opacity duration-150 ${
          showAfterLabel ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        data-tina-field={tinaField(pair, "after")}
        aria-hidden={!showAfterLabel}
        onPointerDown={(event) => event.stopPropagation()}
      >
        After
      </span>

      <input
        type="range"
        min={0}
        max={100}
        value={position}
        onChange={(event) => setPosition(Number(event.target.value))}
        className="sr-only"
        aria-label="Compare before and after"
      />
    </div>
  );
}
